import { useCallback, useEffect, useRef, useState } from "react";
import { stableStepDataEquals } from "../utils/stableStepDataEquals";

export interface UseStepConfigPanelOptions<T> {
    transformDraft?: (prev: T, partial: Partial<T>) => T;
    /** Push each merged draft to the workflow store for the canvas; draft resets from server only when `nodeId` changes. */
    syncDraftToStore?: boolean;
}

export interface UseStepConfigPanelResult<T> {
    draftData: T;
    updateDraft: (partial: Partial<T>) => void;
    commitDraft: (override?: Partial<T>) => void;
    hasDraftChanges: boolean;
    setInitialData: (data: T) => void;
}

export function useStepConfigPanel<T extends object>(
    nodeId: string,
    persistedData: T,
    updateStepData: (stepId: string, data: Partial<T>) => void,
    options?: UseStepConfigPanelOptions<T>
): UseStepConfigPanelResult<T> {
    const [initialData, setInitialData] = useState<T>(persistedData);
    const [draftData, setDraftData] = useState<T>(persistedData);
    const transformDraft = options?.transformDraft;
    const syncDraftToStore = options?.syncDraftToStore ?? false;
    const previousNodeIdForSyncRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (syncDraftToStore) {
            if (previousNodeIdForSyncRef.current !== nodeId) {
                previousNodeIdForSyncRef.current = nodeId;
                setInitialData(persistedData);
                setDraftData(persistedData);
            }
            return;
        }
        setInitialData(persistedData);
        setDraftData(persistedData);
    }, [syncDraftToStore, nodeId, persistedData]);

    const updateDraft = useCallback(
        (partial: Partial<T>) => {
            let mergedDraftAfterPartial: T | undefined;
            setDraftData((previousDraft) => {
                const mergedDraft = transformDraft
                    ? transformDraft(previousDraft, partial)
                    : { ...previousDraft, ...partial };
                if (syncDraftToStore && nodeId) mergedDraftAfterPartial = mergedDraft;
                return mergedDraft;
            });
            if (syncDraftToStore && nodeId && mergedDraftAfterPartial !== undefined) {
                updateStepData(nodeId, mergedDraftAfterPartial as Partial<T>);
            }
        },
        [transformDraft, syncDraftToStore, nodeId, updateStepData]
    );

    const commitDraft = useCallback(
        (override?: Partial<T>) => {
            if (!nodeId) return;
            setDraftData((currentDraft) => {
                const committedStepData = override
                    ? ({ ...currentDraft, ...override } as T)
                    : currentDraft;
                updateStepData(nodeId, committedStepData as Partial<T>);
                setInitialData(committedStepData);
                return committedStepData;
            });
        },
        [nodeId, updateStepData]
    );

    const hasDraftChanges = !stableStepDataEquals(initialData, draftData);

    return {
        draftData,
        updateDraft,
        commitDraft,
        hasDraftChanges,
        setInitialData,
    };
}
